/**
 * Fetches remote game configuration (`GameConfig.json`) with retries and falls back to
 * locally built defaults when the fetch fails or the response omits `BuildVersion`.
 *
 * @module App/Helpers/gameLpDefaultsHandler
 */
import * as Sentry from '@sentry/react';
import { captureCustomException } from 'App/Services/ErrorTracking/captureCustomException';

import { isProd } from './enviroments';

/** Maximum number of retries after the first failed fetch attempt. */
const MAX_RETRIES = 1;
/** Delay before a retryable fetch failure is retried. */
const FETCH_RETRY_DELAY_MS = 400;

/**
 * @param {number} ms
 * @returns {Promise<void>}
 */
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Whether a failed HTTP response (or network error) should trigger another attempt.
 * Retries on missing status, 429, and 5xx; does not retry other 4xx errors.
 *
 * @param {Error & { status?: number }} error
 * @returns {boolean}
 */
const isRetryableFailure = (error) => {
    const status = error?.status;

    if (status == null) return true; // network/parse error, retry
    if (status === 429) return true; // rate limit, retry after delay
    if (status >= 400 && status < 500) return false; // other 4xx: do not retry

    return true; // 5xx etc.: retry
};

/**
 * Builds the default game configuration object used during game initialization.
 * Combines GA preset values with build, localization, and addressables settings.
 * Build iteration is included only when explicitly provided.
 *
 * @param {Object} params
 * @param {string} params.domain - Base domain used to construct addressable paths
 * @param {Object} params.gaPreset - Base Game Analytics preset configuration
 * @param {string} params.languages - Comma-separated list of active languages (e.g. "EN,PT,BG")
 * @param {string} params.buildVersion - Game build version identifier
 * @param {string|number|undefined} params.buildIteration - Optional build iteration identifier
 *
 * @returns {Object} - Default game configuration object
 */
const getLocalDefaults = ({
    domain,
    gaPreset,
    languages,
    buildVersion,
    buildIteration,
}) => {
    const iterationSuffix = buildIteration ? `/${buildIteration}` : '';

    return {
        ...gaPreset,
        BuildVersion: buildVersion,
        ...(buildIteration && { BuildIteration: buildIteration }),
        AddressablesSettingsPath: `${domain}${isProd() ? '/game/' : '/build/'}${buildVersion}${iterationSuffix}`,
        AddressablesBundlePath: `${domain}/bundle/${buildVersion}${iterationSuffix}/WebGL`,
        ActiveLanguages: languages,
        OfferConfiguration: {
            PWAState: 'NotSupported',
            PushState: 'NotAvailable',
        },
    };
};

/**
 * @param {string} v
 * @returns {string}
 */
export const upper = (v) => v.toUpperCase();

/**
 * Loads `GameConfig.json` from `{domain}/configs/GameConfig.json`, with retries on
 * retryable failures. Logs to Sentry on success and when the remote config lacks a build.
 * On persistent failure or missing `BuildVersion`, falls back to the same shape as `getLocalDefaults`.
 *
 * @param {Object} params
 * @param {string} params.domain - Origin used to build the config URL and default paths
 * @param {Object} params.gaPreset - Base Game Analytics preset merged into fallbacks
 * @param {string} params.buildVersion - Build version when constructing defaults
 * @param {string|number|undefined} params.buildIteration - Optional iteration for paths
 * @param {string[]} params.activeLanguages - Language codes; uppercased and joined for `ActiveLanguages`
 *
 * @returns {Promise<Object>} Remote config with `ActiveLanguages`, or merged defaults from `getLocalDefaults`
 */
export const getDefaults = async ({
    domain,
    gaPreset,
    buildVersion,
    buildIteration,
    activeLanguages,
}) => {
    const languages = activeLanguages.map(upper).join(',');
    const url = `${domain}/configs/GameConfig.json`;

    let lastError;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
            const response = await fetch(url);

            if (!response.ok) {
                const err = new Error(
                    `HTTP ${response.status} - ${response.statusText || 'remote config fetch failed'}`
                );

                err.status = response.status;
                throw err;
            }

            Sentry.withScope((scope) => {
                scope.setLevel('info');
                scope.setTag('app', 'remote_config');
                scope.setTag('remote_config_retry', attempt > 0);
                Sentry.captureMessage('AWS Remote Config was Fetched');
            });

            const config = await response.json();

            if (config.BuildVersion) {
                return { ...config, ActiveLanguages: languages };
            } else {
                Sentry.withScope((scope) => {
                    scope.setLevel('warning');
                    scope.setTag('app', 'remote_config');
                    Sentry.captureMessage(
                        'Game build is missing in remote config'
                    );
                });

                return getLocalDefaults({
                    domain,
                    gaPreset,
                    languages,
                    buildVersion,
                    buildIteration,
                });
            }
        } catch (configError) {
            lastError = configError;

            const shouldRetry =
                attempt < MAX_RETRIES && isRetryableFailure(configError);

            if (shouldRetry) {
                await sleep(FETCH_RETRY_DELAY_MS);
            } else {
                break;
            }
        }
    }

    if (lastError) {
        captureCustomException(lastError, 'RemoteConfigFetchError');
        return getLocalDefaults({
            domain,
            gaPreset,
            languages,
            buildVersion,
            buildIteration,
        });
    }
};
